import { type FC, useCallback } from 'react';
import * as uuid from 'uuid';
import { BsPlus } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { DragDropContext } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import cx from 'clsx';

import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import CopyInput from '@editor2/components/CopyInputWrapper';
import Metadata from '@editor2/components/Metadata';
import { type MCChoicePayload, type MCQuestion, ElementType } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { mcQuestionElement } from './styles';
import McChoice from './MCChoice';

import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import { elementCommonStyles } from '@editor2/styles/element';

interface Props {
	element: MCQuestion;
	courseId: number;
}

const McQuestionElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	// Mapper for hook consistency - simply extracts the field we need to track locally
	const questionMapper = useCallback(({ body }: MCQuestion) => ({ body }), []);

	const [questionState, handleLocalChange] = useDebounceProtectedState(element, questionMapper);

	const onChange = (value) => {
		dispatch(updateElement({ ...commonPayload, ...value }));
	};
	const onDebouncedChange = (value) => {
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleQuestionBodyChange = (value: string) => {
		handleLocalChange({ body: value });
		onDebouncedChange({ body: value });
	};

	const handleDragEnd = (result) => {
		const { source, destination, type } = result;
		const { index: sourceIndex } = source;
		const { index: destinationIndex } = destination;

		if (type === 'MCChoice') {
			const choices = [
				...element.choices.slice(0, sourceIndex),
				...element.choices.slice(sourceIndex + 1)
			];

			onChange({
				choices: [
					...choices.slice(0, destinationIndex),
					element.choices[sourceIndex],
					...choices.slice(destinationIndex)
				]
			});
		}
	};

	const handleAddChoiceClick = () => {
		const newChoice: MCChoicePayload = {
			family_id: uuid.v4(),
			is_correct: false,
			body: '',
			rejoinder: '',
			metadata: {},
			type: ElementType.MCChoice
		};

		onChange({ choices: [...element.choices, newChoice] });
	};

	return (
		<Form className={cx(mcQuestionElement, elementCommonStyles)}>
			<CopyInput label="Family ID" value={element.family_id} />

			<Metadata
				courseId={courseId}
				metadata={element.metadata}
				onDebouncedChange={onDebouncedChange}
			/>

			<Form.Group>
				<CodeMirrorHTML
					value={questionState.body}
					showTips={false}
					options={{ placeholder: 'Question', lineNumbers: false, gutters: [] }}
					onChange={(value) => handleQuestionBodyChange(value)}
				/>
			</Form.Group>

			<DragDropContext onDragEnd={handleDragEnd}>
				<DelayedDroppableWrapper droppableId={element.family_id} type="MCChoice">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps} className="choicesContainer">
							{element.choices.map((choice, i) => (
								<McChoice
									choice={choice}
									courseId={courseId}
									element={element}
									index={i}
									key={choice.family_id}
								/>
							))}
							{provided.placeholder}
						</div>
					)}
				</DelayedDroppableWrapper>
			</DragDropContext>

			<Button
				variant="outline-secondary"
				onClick={() => handleAddChoiceClick()}
				className="addChoiceButton">
				<BsPlus />
				Add choice
			</Button>
		</Form>
	);
};

export default McQuestionElement;
